import { usePageLoading } from '@/components/layout/PageLoadingProvider'
import { useEffect, useId, useMemo, useState, type ChangeEvent, type FormEvent } from 'react'
import { Camera, FilePenLine, Play } from 'lucide-react'
import {
  isCitizenReportsConfigured,
  REPORT_PHOTO_ACCEPT,
  REPORT_VIDEO_ACCEPT,
  submitCitizenReport,
  validateCitizenReportFile,
} from '@/lib/data/citizenReports'
import { getVotePriorityCategories } from '@/lib/data/votePriority'
import {
  getTerritoryCantonSlugOptions,
  getTerritoryProvinceSlugOptions,
} from '@/lib/data/territoryCatalogs'
import type { InferredTerritory } from '@/lib/data/territoryLocation'
import type { CitizenReportAttachmentKind, CitizenReportSubmitStage } from '@/types/citizenReport'
import type { VotePriorityCategory } from '@/types/votePriority'
import { trackEvent } from '@/analytics'

interface CitizenReportFormProps {
  territory?: InferredTerritory | null
  compact?: boolean
  title?: string
  suppressPageLoading?: boolean
  onCollapse?: () => void
}

const stageLabels: Record<CitizenReportSubmitStage, string> = {
  creating: 'Preparando reporte...',
  'uploading-photo': 'Subiendo foto...',
  'uploading-video': 'Subiendo video...',
  finalizing: 'Registrando reporte...',
}

export function CitizenReportForm({
  territory,
  compact = false,
  title = 'Envía tu reporte',
  suppressPageLoading = false,
  onCollapse,
}: CitizenReportFormProps) {
  const photoInputId = useId()
  const videoInputId = useId()
  const [photo, setPhoto] = useState<File | null>(null)
  const [video, setVideo] = useState<File | null>(null)
  const [isWriting, setIsWriting] = useState(false)
  const [description, setDescription] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [selectedLocationKey, setSelectedLocationKey] = useState('')
  const [categories, setCategories] = useState<VotePriorityCategory[]>([])
  const [categoryStatus, setCategoryStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  usePageLoading(!suppressPageLoading && categoryStatus === 'loading')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [fileError, setFileError] = useState('')
  const [submitMessage, setSubmitMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const reportsConfigured = isCitizenReportsConfigured()
  const provinceOptions = useMemo(() => getTerritoryProvinceSlugOptions(), [])
  const cantonOptions = useMemo(() => getTerritoryCantonSlugOptions(), [])
  const selectedCanton = useMemo(() => {
    const [provinceId, cantonId] = selectedLocationKey.split('::')
    return cantonOptions.find(
      (canton) => canton.provinceId === provinceId && canton.id === cantonId,
    )
  }, [cantonOptions, selectedLocationKey])
  const selectedTerritory = useMemo<InferredTerritory | null>(() => {
    if (!selectedCanton) return territory ?? null
    const province = provinceOptions.find((item) => item.id === selectedCanton.provinceId)
    if (!province) return null

    return {
      provinceId: province.id,
      provinceName: province.label,
      cantonId: selectedCanton.id,
      cantonName: selectedCanton.label,
      nearestRecintoId: '',
      distanceKm: 0,
    }
  }, [provinceOptions, selectedCanton, territory])

  useEffect(() => {
    if (!territory || selectedLocationKey) return
    setSelectedLocationKey(`${territory.provinceId}::${territory.cantonId}`)
  }, [selectedLocationKey, territory])

  useEffect(() => {
    let ignore = false

    getVotePriorityCategories()
      .then((response) => {
        if (ignore) return
        setCategories(response.categories ?? [])
        setCategoryStatus('ready')
      })
      .catch(() => {
        if (!ignore) setCategoryStatus('error')
      })

    return () => {
      ignore = true
    }
  }, [])

  function selectFile(kind: CitizenReportAttachmentKind, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null

    if (!file) {
      if (kind === 'photo') setPhoto(null)
      else setVideo(null)
      return
    }

    const error = validateCitizenReportFile(kind, file)
    setFileError(error)
    if (error) {
      if (kind === 'photo') setPhoto(null)
      else setVideo(null)
      event.target.value = ''
      return
    }

    if (kind === 'photo') setPhoto(file)
    else setVideo(file)
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!reportsConfigured) {
      setSubmitMessage('El canal de recepción todavía no está configurado.')
      return
    }
    if (!selectedTerritory) {
      setSubmitMessage('Selecciona un cantón para asociar el reporte a una ciudad.')
      return
    }
    if (!selectedCategory) {
      setSubmitMessage('Selecciona un tema para el reporte.')
      return
    }
    if (description.trim().length < 20) {
      setIsWriting(true)
      setSubmitMessage('Escribe una descripción de al menos 20 caracteres.')
      return
    }
    if (fileError || isSubmitting) return

    const form = event.currentTarget
    setIsSubmitting(true)
    setSubmitMessage('Preparando reporte...')

    try {
      const result = await submitCitizenReport(
        {
          topicId: selectedCategory,
          description: description.trim(),
          territory: selectedTerritory,
          contactName: contactName.trim(),
          contactEmail: contactEmail.trim(),
          contactPhone: contactPhone.trim(),
          photo,
          video,
        },
        (stage) => setSubmitMessage(stageLabels[stage]),
      )

      setSubmitMessage(`Reporte ${result.reportId} recibido para revisión editorial.`)
      setPhoto(null)
      setVideo(null)
      setDescription('')
      setContactName('')
      setContactEmail('')
      setContactPhone('')
      setSelectedCategory('')
      setIsWriting(false)
      setFileError('')
      form.reset()
      trackEvent('citizen_report_submit', {
        event_category: 'participation',
        submission_status: 'success',
        topic_id: selectedCategory,
        province_id: selectedTerritory.provinceId,
        canton_id: selectedTerritory.cantonId,
        has_photo: Boolean(photo),
        has_video: Boolean(video),
      })
    } catch (error) {
      setSubmitMessage(String((error as Error).message || error))
      trackEvent('citizen_report_submit', {
        event_category: 'participation',
        submission_status: 'request_error',
        topic_id: selectedCategory,
        has_photo: Boolean(photo),
        has_video: Boolean(video),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className={`citizen-report-form ${compact ? 'citizen-report-form--compact' : ''}`} onSubmit={handleSubmit}>
      <header>
        <h2>{title}</h2>
        <p>
          {compact
            ? '¿Qué está pasando en tu barrio? ¿Qué harías en tu gestión? Comparte tu estrategia para mejorar tu ciudad.'
            : '¿Qué está pasando en tu barrio?'}
        </p>
        {onCollapse ? (
          <button
            className="citizen-report-form__collapse"
            type="button"
            aria-expanded="true"
            onClick={onCollapse}
          >
            Ocultar formulario
          </button>
        ) : null}
      </header>

      <label className="citizen-report-form__field citizen-report-form__field--contact-name">
        <span>Nombre o apodo (opcional)</span>
        <input
          type="text"
          name="contactName"
          autoComplete="name"
          maxLength={100}
          value={contactName}
          disabled={isSubmitting}
          onChange={(event) => setContactName(event.target.value)}
        />
      </label>

      <label className="citizen-report-form__field citizen-report-form__field--contact-email">
        <span>Correo (opcional)</span>
        <input
          type="email"
          name="contactEmail"
          autoComplete="email"
          maxLength={254}
          value={contactEmail}
          disabled={isSubmitting}
          onChange={(event) => setContactEmail(event.target.value)}
        />
      </label>

      <label className="citizen-report-form__field citizen-report-form__field--contact-phone">
        <span>Número de contacto (opcional)</span>
        <input
          type="tel"
          name="contactPhone"
          autoComplete="tel"
          inputMode="tel"
          minLength={7}
          maxLength={30}
          pattern="[0-9+() .-]{7,30}"
          title="Usa entre 7 y 30 caracteres: números, espacios, +, paréntesis, puntos o guiones."
          value={contactPhone}
          disabled={isSubmitting}
          onChange={(event) => setContactPhone(event.target.value)}
        />
      </label>

      <button
        className="citizen-report-form__action citizen-report-form__action--write"
        type="button"
        aria-expanded={isWriting}
        disabled={isSubmitting}
        onClick={() => setIsWriting((current) => !current)}
      >
        <FilePenLine aria-hidden="true" />
        <span>Escribir reporte</span>
      </button>

      {isWriting ? (
        <label className="citizen-report-form__textarea">
          <span>Reporte</span>
          <textarea
            required
            minLength={20}
            maxLength={1000}
            rows={5}
            value={description}
            disabled={isSubmitting}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
      ) : null}

      <input
        className="citizen-report-form__file"
        id={photoInputId}
        type="file"
        accept={REPORT_PHOTO_ACCEPT}
        disabled={isSubmitting}
        onChange={(event) => selectFile('photo', event)}
      />
      <label className="citizen-report-form__action citizen-report-form__action--photo" htmlFor={photoInputId}>
        <Camera aria-hidden="true" />
        <span>{photo?.name || 'Añadir foto'}</span>
      </label>

      <input
        className="citizen-report-form__file"
        id={videoInputId}
        type="file"
        accept={REPORT_VIDEO_ACCEPT}
        disabled={isSubmitting}
        onChange={(event) => selectFile('video', event)}
      />
      <label className="citizen-report-form__action citizen-report-form__action--video" htmlFor={videoInputId}>
        <Play aria-hidden="true" />
        <span>{video?.name || 'Añadir video'}</span>
      </label>

      <label className="citizen-report-form__field citizen-report-form__field--location">
        <span>Ubicación</span>
        <select
          required
          value={selectedLocationKey}
          disabled={isSubmitting}
          onChange={(event) => setSelectedLocationKey(event.target.value)}
        >
          <option value="">Selecciona un cantón</option>
          {provinceOptions.map((province) => (
            <optgroup key={province.id} label={province.label}>
              {cantonOptions
                .filter((canton) => canton.provinceId === province.id)
                .map((canton) => (
                  <option key={`${province.id}-${canton.id}`} value={`${province.id}::${canton.id}`}>
                    {canton.label}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </label>

      <label className="citizen-report-form__field citizen-report-form__field--topic">
        <span>Tema</span>
        <select
          value={selectedCategory}
          disabled={isSubmitting || categoryStatus !== 'ready' || !categories.length}
          onChange={(event) => setSelectedCategory(event.target.value)}
        >
          <option value="">
            {categoryStatus === 'loading'
              ? 'Cargando temas...'
              : categoryStatus === 'error'
                ? 'Temas no disponibles'
                : 'Selecciona un tema'}
          </option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.label}
            </option>
          ))}
        </select>
      </label>

      <button
        className="citizen-report-form__submit"
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Enviando...' : 'Enviar reporte'}
      </button>

      {fileError ? (
        <p className="citizen-report-form__status citizen-report-form__status--error" role="alert">
          {fileError}
        </p>
      ) : null}
      {submitMessage ? (
        <p className="citizen-report-form__status" role="status" aria-live="polite">
          {submitMessage}
        </p>
      ) : null}
      {!reportsConfigured ? (
        <p className="citizen-report-form__status">Canal de recepción pendiente de configuración.</p>
      ) : null}

      <p className="citizen-report-form__notice">
        {compact
          ? 'Los reportes pasan por revisión editorial. No publiques datos personales ni información sensible.'
          : 'Los datos de contacto son opcionales y se usarán únicamente para seguimiento editorial. Los reportes pasan por revisión antes de publicarse.'}
      </p>
    </form>
  )
}
